import { useState, useCallback } from "react";

// ═══════════════════════════════════════════════════════════════
// 1) CC/MCC SINIFLANDIRMA SÖZLÜĞÜ
// Komplikasyon/Komorbidite ağırlığı → LOS çarpanı
// ═══════════════════════════════════════════════════════════════
const CC_MCC_MAP = {
  // MCC (Major Complication/Comorbidity) — LOS +4
  "N17":  { level: "MCC", label: "Akut böbrek yetmezliği",        los_add: 4 },
  "J96":  { level: "MCC", label: "Solunum yetmezliği",             los_add: 4 },
  "R65.2":{ level: "MCC", label: "Septik şok",                     los_add: 4 },
  "A41":  { level: "MCC", label: "Sepsis",                         los_add: 4 },
  "I21":  { level: "MCC", label: "Akut miyokard enfarktüsü",       los_add: 4 },
  "G93.1":{ level: "MCC", label: "Anoksik beyin hasarı",           los_add: 4 },
  "K91.3":{ level: "MCC", label: "Postoperatif ileus",             los_add: 3 },
  "O15":  { level: "MCC", label: "Eklampsi",                       los_add: 4 },
  "O72.0":{ level: "MCC", label: "3. evre PPH",                    los_add: 3 },
  // CC (Complication/Comorbidity) — LOS +2
  "I50":  { level: "CC",  label: "Kalp yetmezliği",                los_add: 2 },
  "E11":  { level: "CC",  label: "Tip 2 diyabet",                  los_add: 2 },
  "J44":  { level: "CC",  label: "KOAH",                           los_add: 2 },
  "N18":  { level: "CC",  label: "Kronik böbrek hastalığı",         los_add: 2 },
  "K65":  { level: "CC",  label: "Peritonit",                      los_add: 2 },
  "T81":  { level: "CC",  label: "Postoperatif komplikasyon",      los_add: 2 },
  "O14":  { level: "CC",  label: "Preeklampsi",                    los_add: 2 },
  "P07":  { level: "CC",  label: "Prematür yenidoğan",             los_add: 2 },
  "Z87.891":{ level:"CC", label: "Önceki cerrahi öykü",            los_add: 1 },
};

function getCCMCC(secondaryList = []) {
  const found = [];
  for (const code of secondaryList) {
    for (const [prefix, info] of Object.entries(CC_MCC_MAP)) {
      if (code.startsWith(prefix)) {
        found.push({ code, ...info });
        break;
      }
    }
  }
  const mcc = found.filter(f => f.level === "MCC");
  const cc  = found.filter(f => f.level === "CC");
  const los_add = found.reduce((s, f) => s + f.los_add, 0);
  return { mcc, cc, all: found, los_add };
}

// ═══════════════════════════════════════════════════════════════
// 2) SUT PAKET TANIMI (Paket içi kalem çakışma kontrolü)
// ═══════════════════════════════════════════════════════════════
const SUT_PACKAGES = {
  PKG_APPENDECTOMY_LAP: {
    label: "Laparoskopik Apendektomi Paketi",
    included_np: ["NP001"],
    includes_items: ["ameliyathane", "anestezi", "anastezi", "anestezist", "lap set", "laparoskopi",
      "trokar", "port", "staplar", "stapler", "disposable", "yatış", "standart yatış"],
    excluded_ok: ["NP010","NP204","NP112","kan ürünü", "yoğun bakım", "icu","ybu","dren", "abse",
      "komplikasyon","revisyon"],
  },
  PKG_APPENDECTOMY_OPEN: {
    label: "Açık Apendektomi Paketi",
    included_np: ["NP002"],
    includes_items: ["ameliyathane", "anestezi", "standart yatış"],
    excluded_ok: ["NP010","NP204","kan ürünü","yoğun bakım","dren","komplikasyon"],
  },
  PKG_CESAREAN: {
    label: "Sezaryen Paketi",
    included_np: ["NP101"],
    includes_items: ["ameliyathane", "anestezi", "epidural", "spinal", "yenidoğan bakımı",
      "standart yatış", "bebek bakımı", "loğusa"],
    excluded_ok: ["NP110","NP111","NP112","NP204","kan ürünü","yoğun bakım","pph","transfüzyon",
      "preeklampsi","eklampsi","nicu","komplikasyon"],
  },
};

function detectPackageConflicts(procedures_np = [], billing_items = []) {
  const flags = [];
  let active_pkg = null;

  for (const [pkg_key, pkg] of Object.entries(SUT_PACKAGES)) {
    if (procedures_np.some(np => pkg.included_np.includes(np))) {
      active_pkg = { key: pkg_key, ...pkg };
      break;
    }
  }
  if (!active_pkg) return { flags, active_pkg: null };

  for (const item of billing_items) {
    const desc = (item.desc || "").toLowerCase();
    const isIncluded = active_pkg.includes_items.some(kw => desc.includes(kw.toLowerCase()));
    const isExcludedOk = active_pkg.excluded_ok.some(kw => desc.includes(kw.toLowerCase()));
    if (isIncluded && !isExcludedOk && item.amount > 0) {
      flags.push({
        level: "error",
        msg: `PAKET ÇAKIŞMASI: "${item.desc}" → ${active_pkg.label} içinde zaten dahil → KESİLMELİ (${item.amount.toLocaleString("tr-TR")}₺)`,
        amount: item.amount,
        item_id: item.id || item.sut_code,
      });
    }
  }
  return { flags, active_pkg };
}

// ═══════════════════════════════════════════════════════════════
// 3) TEKRAR YATIŞ KONTROLÜ (30 gün aynı tanı grubu)
// ═══════════════════════════════════════════════════════════════
function checkReadmission(currentCase, caseHistory = []) {
  const flags = [];
  const admitDate = new Date(currentCase.dates?.admit);
  const primaryPrefix = currentCase.icd10_primary?.substring(0, 3);

  for (const prev of caseHistory) {
    const prevDischarge = new Date(prev.dates?.discharge);
    const daysDiff = Math.round((admitDate - prevDischarge) / 86400000);
    const prevPrefix = prev.icd10_primary?.substring(0, 3);
    if (daysDiff >= 0 && daysDiff <= 30 && prevPrefix === primaryPrefix) {
      flags.push({
        level: "error",
        msg: `30 GÜN İÇİ TEKRAR YATIŞ: Önceki taburculuk ${prev.dates.discharge} (${daysDiff} gün önce, ${prevPrefix}) → Tedavi yeterliliği sorgulanmalı`,
        daysDiff,
        prev_case_id: prev.case_id,
      });
    }
    if (daysDiff >= 0 && daysDiff <= 90 && prevPrefix === primaryPrefix && daysDiff > 30) {
      flags.push({
        level: "warn",
        msg: `90 GÜN İÇİ TEKRAR YATIŞ: ${prev.dates.discharge} (${daysDiff} gün önce) — izlem kaydı mevcut mu?`,
        daysDiff,
        prev_case_id: prev.case_id,
      });
    }
  }
  return flags;
}

// ═══════════════════════════════════════════════════════════════
// 4) NL DBC/DOT MAPPING
// ═══════════════════════════════════════════════════════════════
const NL_DBC_MAP = {
  // Solunumu
  "14B780": { np: "NP_CAP_ADMIT", label: "Longontsteking (CAP) – klinisch opname", icd10_equiv: "J18.9" },
  "14B781": { np: "NP_CAP_ICU",   label: "Longontsteking – IC opname",             icd10_equiv: "J18.9" },
  // Chirurgie
  "11C360": { np: "NP001", label: "Appendectomie – laparoscopisch",     icd10_equiv: "K35.8" },
  "11C361": { np: "NP002", label: "Appendectomie – open",               icd10_equiv: "K35.8" },
  "11C362": { np: "NP010", label: "Appendectomie – gecompliceerd",      icd10_equiv: "K35.2" },
  // Verloskunde
  "17B030": { np: "NP101", label: "Keizersnede – electief",             icd10_equiv: "O82" },
  "17B031": { np: "NP101", label: "Keizersnede – spoed",                icd10_equiv: "O82" },
  "17B032": { np: "NP110", label: "Keizersnede – complicaties PPH",     icd10_equiv: "O72" },
  "17B033": { np: "NP111", label: "Keizersnede – pre-eclampsie",        icd10_equiv: "O14" },
  // IC
  "IC_DAY": { np: "NP204", label: "IC dag (intensive care)",            icd10_equiv: null },
  "NICU_DAY":{ np:"NP112", label: "NICU dag (neonatale IC)",            icd10_equiv: null },
};

const NL_EXPECTED_LOS = {
  "14B780": { base: 6,  cc_add: 2, mcc_add: 4 },
  "14B781": { base: 10, cc_add: 2, mcc_add: 3 },
  "11C360": { base: 2,  cc_add: 1, mcc_add: 2 },
  "11C361": { base: 3,  cc_add: 1, mcc_add: 2 },
  "11C362": { base: 5,  cc_add: 2, mcc_add: 3 },
  "17B030": { base: 3,  cc_add: 1, mcc_add: 2 },
  "17B031": { base: 3,  cc_add: 2, mcc_add: 3 },
  "17B032": { base: 5,  cc_add: 1, mcc_add: 2 },
  "17B033": { base: 5,  cc_add: 2, mcc_add: 3 },
};

function runNLAudit(nlCase) {
  const dbc = nlCase.dbc_code;
  const mapping = NL_DBC_MAP[dbc];
  if (!mapping) return { error: true, message: `DBC kodu ${dbc} bu MVP'de tanımlı değil.` };

  const ccmcc = getCCMCC(nlCase.icd10_secondary || []);
  const losTable = NL_EXPECTED_LOS[dbc] || { base: 5, cc_add: 2, mcc_add: 3 };
  const expectedLOS = losTable.base
    + (ccmcc.mcc.length > 0 ? losTable.mcc_add : 0)
    + (ccmcc.cc.length > 0 && ccmcc.mcc.length === 0 ? losTable.cc_add : 0);

  const actualLOS = nlCase.actual_los || 0;
  const losExtended = actualLOS > expectedLOS + 1;

  const flags = [];
  if (losExtended) flags.push({ level: "error", msg: `LOS ${actualLOS}g > verwacht ${expectedLOS}g — onderbouwing vereist` });
  else flags.push({ level: "ok", msg: `LOS ${actualLOS}g acceptabel (verwacht: ${expectedLOS}g)` });

  // DBC volledigheid
  if (!nlCase.hoofddiagnose_present) flags.push({ level: "error", msg: "Hoofddiagnose ontbreekt in DBC declaratie" });
  if (!nlCase.zorgactiviteiten_present) flags.push({ level: "warn",  msg: "Zorgactiviteiten niet volledig aangeleverd" });
  if (!nlCase.ontslagbrief_present) flags.push({ level: "warn", msg: "Ontslagbrief ontbreekt — verplicht voor NL verzekering" });

  const totalClaimed = nlCase.claimed_amount || 0;
  const holdAmount = losExtended ? Math.round(totalClaimed * 0.25) : 0;

  return {
    error: false,
    country: "NL",
    dbc,
    mapping,
    np: mapping.np,
    ccmcc,
    expectedLOS,
    actualLOS,
    losExtended,
    flags,
    totalClaimed,
    holdAmount,
    payAmount: totalClaimed - holdAmount,
    decision: holdAmount > 0 ? "OPSCHORTEN / CONTROLEREN" : "BETALEN",
    decisionColor: holdAmount > 0 ? "red" : "green",
  };
}

// ═══════════════════════════════════════════════════════════════
// 5) TR KURAL MOTORİ (v2 — CC/MCC + Paket + Tekrar Yatış dahil)
// ═══════════════════════════════════════════════════════════════
const INDICATION_RULES = {
  CAP_PNEUMONIA: {
    icd10: ["J12","J13","J14","J15","J16","J18","J69"],
    label: "Pnömoni (CAP)",
    np_codes: ["NP201","NP202","NP203","NP204"],
    checkAdmission: (c) => {
      const f = [];
      if (c.vitals?.spo2 < 92) f.push({ ok: true, msg: `SpO₂ ${c.vitals.spo2}% < 92 → yatış endike` });
      else if (c.vitals?.spo2) f.push({ ok: true, msg: `SpO₂ ${c.vitals.spo2}% normal` });
      if (c.vitals?.rr >= 30) f.push({ ok: true, msg: `SS ${c.vitals.rr}/dk ≥ 30 → yatış endike` });
      if (c.vitals?.sbp < 90) f.push({ ok: true, msg: `SKB ${c.vitals.sbp} mmHg < 90 → yatış endike` });
      if (c.mental_status_change) f.push({ ok: true, msg: "Bilinç değişikliği mevcut" });
      if (c.curb65 >= 2) f.push({ ok: true, msg: `CURB-65: ${c.curb65} ≥ 2 → yatış endike (NICE protokolü)` });
      if (f.length === 0) f.push({ ok: false, msg: "⚠️ Yatış endikasyonu belgelenemedi" });
      return f;
    },
    checkICU: (c) => {
      const f = [];
      if (c.resp_support?.niv) f.push({ ok: true, msg: "NIV uygulanmış → YBÜ endike" });
      if (c.resp_support?.imv) f.push({ ok: true, msg: "İMV uygulanmış → YBÜ endike" });
      if (c.icu?.vasopressor) f.push({ ok: true, msg: "Vazopressör kullanımı → YBÜ endike" });
      return f;
    },
    baseLOS: (c) => 5,
    extendedTrigger: (exp) => exp + 2,
    acceptableExtension: (c) => c.complications?.sepsis || c.complications?.aki || c.icu?.icu_days > 0 || c.resp_support?.imv,
    requiredEvidence: ["SpO₂ kaydı (oda havası)", "Solunum sayısı", "Kan basıncı", "AC grafisi veya BT raporu", "Yatış endikasyon notu"],
    ifICU: ["YBÜ takip formu", "Ventilator/NIV kaydı", "ABG sonuçları", "Vazopressör dozu"],
  },
  ACUTE_APPENDICITIS: {
    icd10: ["K35","K36","K37"],
    label: "Akut Apandisit",
    np_codes: ["NP001","NP002","NP010"],
    checkAdmission: (c) => {
      const f = [];
      if (c.imaging?.ct_abdomen || c.imaging?.us_appendix) f.push({ ok: true, msg: "Görüntüleme ile apandisit doğrulandı" });
      else f.push({ ok: false, msg: "⚠️ Görüntüleme belgesi eksik (BT/US)" });
      if (c.op_note_present) f.push({ ok: true, msg: "Ameliyat notu mevcut" });
      else f.push({ ok: false, msg: "⚠️ Ameliyat notu eksik" });
      if (c.procedure_type) f.push({ ok: true, msg: `Operasyon tipi: ${c.procedure_type === "lap" ? "Laparoskopik" : "Açık"}` });
      return f;
    },
    checkICU: () => [],
    baseLOS: (c) => {
      if (c.complications?.perforation || c.complications?.abscess) return 4;
      if (c.procedure_type === "open") return 3;
      return 2;
    },
    extendedTrigger: (exp, c) => c.complications?.perforation ? 7 : 5,
    acceptableExtension: (c) => c.complications?.perforation || c.complications?.abscess || c.complications?.ileus || c.complications?.sepsis,
    requiredEvidence: ["BT veya US raporu", "Ameliyat notu", "Operasyon tipi (lap/açık)", "Patoloji raporu"],
    ifComplicated: ["Perforasyon/abse kanıtı (op. not + BT)", "Dren kaydı", "Kültür sonuçları"],
  },
  CESAREAN: {
    icd10: ["O82","O14","O15","O44","O45","O72","O75"],
    label: "Sezaryen Doğumu",
    np_codes: ["NP101","NP110","NP111","NP112"],
    checkAdmission: (c) => {
      const f = [];
      if (c.cs_indication_documented) f.push({ ok: true, msg: "Sezaryen endikasyonu belgelenmiş (NICE NG192)" });
      else f.push({ ok: false, msg: "⚠️ Sezaryen endikasyonu belgelenmemiş — NICE NG192 gerektirir" });
      if (c.delivery_note_present) f.push({ ok: true, msg: "Doğum notu mevcut" });
      else f.push({ ok: false, msg: "⚠️ Doğum notu eksik" });
      if (c.ctg_or_partogram) f.push({ ok: true, msg: "CTG/partogram kaydı mevcut" });
      else f.push({ ok: false, msg: "⚠️ CTG/partogram kaydı eksik" });
      return f;
    },
    checkICU: () => [],
    baseLOS: (c) => (c.complications?.pph || c.complications?.infection || c.complications?.severe_preeclampsia || c.nicu_days > 0) ? 5 : 3,
    extendedTrigger: (exp, c) => (c.complications?.pph ? 8 : 6),
    acceptableExtension: (c) => c.complications?.pph || c.complications?.infection || c.complications?.severe_preeclampsia || c.nicu_days > 0,
    requiredEvidence: ["Doğum notu", "Sezaryen endikasyonu", "CTG veya partogram"],
    ifComplicated: ["Kanama/transfüzyon: Hb trendi + kan bankası kaydı", "NICU kaydı (bebek için)", "Enfeksiyon: kültür + antibiyotik eskalasyon"],
  },
};

function runTRAudit(caseData, caseHistory = []) {
  let matchedRule = null, ruleKey = null;
  for (const [key, rule] of Object.entries(INDICATION_RULES)) {
    if (rule.icd10.some(c => caseData.icd10_primary?.startsWith(c))) {
      matchedRule = rule; ruleKey = key; break;
    }
  }
  if (!matchedRule) return { error: true, message: `ICD-10 (${caseData.icd10_primary}) bu MVP kapsamında değil.` };

  const c = { ...caseData.clinical, ...caseData };
  const actualLOS = Math.round(
    (new Date(caseData.dates?.discharge) - new Date(caseData.dates?.admit)) / 86400000
  ) || caseData.clinical?.actual_los || 0;

  // CC/MCC
  const ccmcc = getCCMCC(caseData.icd10_secondary || []);
  const baseLOS = matchedRule.baseLOS(c);
  const expectedLOS = baseLOS + ccmcc.los_add;
  const triggerLOS = matchedRule.extendedTrigger(expectedLOS, c);
  const losExtended = actualLOS > triggerLOS;
  const extensionJustified = matchedRule.acceptableExtension(c);

  const admissionFlags = matchedRule.checkAdmission(c);
  const icuFlags = matchedRule.checkICU(c);

  const losFlags = [];
  if (losExtended) {
    if (extensionJustified) losFlags.push({ level: "warn",  msg: `LOS ${actualLOS}g (beklenti: ${expectedLOS}g) — uzama komplikasyonla açıklanabilir` });
    else                     losFlags.push({ level: "error", msg: `LOS ${actualLOS}g, trigger ${triggerLOS}g üzerinde — gerekçe YOK → ASKIYA AL` });
  } else {
    losFlags.push({ level: "ok", msg: `LOS ${actualLOS}g kabul edilebilir (beklenti: ${expectedLOS}g, CC/MCC eklendi: +${ccmcc.los_add}g)` });
  }

  // Fatura
  const items = caseData.billing?.items || [];
  const billingFlags = [];

  // Mükerrer
  const codeMap = {};
  items.forEach(item => {
    const key = `${item.sut_code}_${item.date}`;
    codeMap[key] = (codeMap[key] || 0) + 1;
  });
  Object.entries(codeMap).forEach(([key, count]) => {
    if (count > 1) billingFlags.push({ level: "error", msg: `MÜKERRER: ${key} → aynı gün ${count}x faturalandırılmış` });
  });

  // YBÜ çapraz
  const icuBilled = items.some(i => /(yoğun|icu|ybu)/i.test(i.desc || ""));
  if (icuBilled && (!c.icu || c.icu.icu_days === 0)) {
    billingFlags.push({ level: "error", msg: "YBÜ faturalandırılmış ama epikrizde YBÜ kaydı yok → ASKIYA AL" });
  }

  // Oda limiti
  const dailyRoom = caseData.billing?.daily_room_price;
  const roomLimit = caseData.policy?.limits?.daily_room_max;
  if (dailyRoom && roomLimit && dailyRoom > roomLimit) {
    const excess = (dailyRoom - roomLimit) * actualLOS;
    billingFlags.push({ level: "warn", msg: `Oda ücreti ${dailyRoom.toLocaleString()}₺/g > limit ${roomLimit.toLocaleString()}₺/g → fark ${excess.toLocaleString()}₺ kesilir` });
  }

  // Paket çakışması
  const procedures_np = (caseData.clinical?.procedures_np || []);
  const pkgResult = detectPackageConflicts(procedures_np, items);
  pkgResult.flags.forEach(f => billingFlags.push(f));

  // Tekrar yatış
  const readmissionFlags = checkReadmission(caseData, caseHistory);

  // Eksik kanıt
  const missingEvidence = [];
  if (ruleKey === "CAP_PNEUMONIA") {
    if (!c.vitals?.spo2) missingEvidence.push("SpO₂ kaydı");
    if (!c.imaging?.cxr_infiltrate && !c.imaging?.ct_chest) missingEvidence.push("AC grafisi/BT raporu");
    if (icuBilled && !c.resp_support?.niv && !c.resp_support?.imv) missingEvidence.push("Ventilasyon/NIV kaydı");
  }
  if (ruleKey === "ACUTE_APPENDICITIS") {
    if (!c.op_note_present) missingEvidence.push("Ameliyat notu");
    if (!c.imaging?.ct_abdomen && !c.imaging?.us_appendix) missingEvidence.push("BT/US raporu");
  }
  if (ruleKey === "CESAREAN") {
    if (!c.delivery_note_present) missingEvidence.push("Doğum notu");
    if (!c.cs_indication_documented) missingEvidence.push("Sezaryen endikasyon belgesi");
    if (!c.ctg_or_partogram) missingEvidence.push("CTG/Partogram");
  }

  const hasError = losFlags.some(f => f.level==="error") || billingFlags.some(f => f.level==="error") || missingEvidence.length > 0 || readmissionFlags.some(f => f.level==="error");
  const hasWarn  = losFlags.some(f => f.level==="warn")  || billingFlags.some(f => f.level==="warn")  || readmissionFlags.some(f => f.level==="warn");

  let decision = "ÖDE"; let decisionColor = "green";
  if (hasError) { decision = "ASKIYA AL / İNCELE"; decisionColor = "red"; }
  else if (hasWarn) { decision = "KISMI ÖDE / İNCELE"; decisionColor = "orange"; }

  const totalBilled = items.reduce((s, i) => s + (i.amount || 0), 0);
  const pkgHold = pkgResult.flags.reduce((s, f) => s + (f.amount || 0), 0);
  const roomHold = (dailyRoom && roomLimit && dailyRoom > roomLimit) ? (dailyRoom - roomLimit) * actualLOS : 0;
  const losHold = (losExtended && !extensionJustified) ? Math.round(totalBilled * 0.25) : 0;
  const holdAmount = Math.min(totalBilled, pkgHold + roomHold + losHold);
  const payAmount = totalBilled - holdAmount;

  return {
    error: false, country: "TR", ruleKey, label: matchedRule.label, decision, decisionColor,
    actualLOS, expectedLOS, baseLOS, ccmcc, losExtended, extensionJustified,
    totalBilled, payAmount, holdAmount, pkgHold, roomHold, losHold,
    admissionFlags, icuFlags, losFlags, billingFlags, readmissionFlags, missingEvidence,
    requiredEvidence: matchedRule.requiredEvidence,
    ifICU: matchedRule.ifICU, ifComplicated: matchedRule.ifComplicated,
    active_pkg: pkgResult.active_pkg,
  };
}

// ═══════════════════════════════════════════════════════════════
// ÖRNEK VAKALAR
// ═══════════════════════════════════════════════════════════════
const SAMPLE_CASES = {
  cap_fraud: {
    label: "🚨 TR · Pnömoni — LOS + YBÜ Dolandırıcılığı",
    country: "TR",
    data: {
      case_id: "TR-002", patient: { age: 52, sex: "K" },
      provider: { name: "Premium Sağlık A.Ş." },
      dates: { admit: "2026-01-05", discharge: "2026-01-19" },
      icd10_primary: "J18.9", icd10_secondary: [],
      clinical: {
        vitals: { spo2: 95, rr: 20, sbp: 130 }, mental_status_change: false, curb65: 1,
        imaging: { cxr_infiltrate: true },
        resp_support: { oxygen: false, niv: false, imv: false },
        icu: { icu_days: 0, vasopressor: false },
        complications: { sepsis: false, aki: false },
        procedures_np: ["NP201"],
        actual_los: 14,
      },
      billing: {
        daily_room_price: 3500,
        items: [
          { sut_code: "SUT001", desc: "Yatış günü", qty: 14, unit_price: 3500, amount: 49000, date: "2026-01-05" },
          { sut_code: "SUT004", desc: "Yoğun Bakım Ünitesi Günü", qty: 3, unit_price: 8000, amount: 24000, date: "2026-01-10" },
          { sut_code: "SUT001", desc: "Yatış günü", qty: 2, unit_price: 3500, amount: 7000, date: "2026-01-05" },
        ]
      },
      policy: { limits: { daily_room_max: 2000 } }
    },
    history: []
  },
  cap_cc: {
    label: "✅ TR · Pnömoni — CC/MCC ile haklı uzamış LOS",
    country: "TR",
    data: {
      case_id: "TR-005", patient: { age: 74, sex: "E" },
      provider: { name: "Acıbadem Maslak" },
      dates: { admit: "2026-02-01", discharge: "2026-02-10" },
      icd10_primary: "J18.9", icd10_secondary: ["I50.9","N17.9"],
      clinical: {
        vitals: { spo2: 88, rr: 34, sbp: 85 }, mental_status_change: true, curb65: 4,
        imaging: { cxr_infiltrate: true, ct_chest: true },
        resp_support: { oxygen: true, niv: true, imv: false },
        icu: { icu_days: 3, vasopressor: false },
        complications: { sepsis: false, aki: true },
        procedures_np: ["NP201","NP202","NP204"],
        actual_los: 9,
      },
      billing: {
        daily_room_price: 1800,
        items: [
          { sut_code: "SUT001", desc: "Yatış günü", qty: 9, unit_price: 1800, amount: 16200, date: "2026-02-01" },
          { sut_code: "SUT004", desc: "Yoğun Bakım Ünitesi Günü", qty: 3, unit_price: 6000, amount: 18000, date: "2026-02-04" },
          { sut_code: "SUT002", desc: "NIV tedavisi", qty: 3, unit_price: 800, amount: 2400, date: "2026-02-04" },
        ]
      },
      policy: { limits: { daily_room_max: 2500 } }
    },
    history: []
  },
  appendicitis_pkg: {
    label: "⚠️ TR · Apandisit — Paket İçi Kalem Çakışması",
    country: "TR",
    data: {
      case_id: "TR-003", patient: { age: 28, sex: "E" },
      provider: { name: "Özel Cerrahi Merkezi" },
      dates: { admit: "2026-02-10", discharge: "2026-02-15" },
      icd10_primary: "K35.8", icd10_secondary: [],
      clinical: {
        imaging: { ct_abdomen: true },
        op_note_present: true, procedure_type: "lap",
        complications: { perforation: false, abscess: false },
        procedures_np: ["NP001"],
        actual_los: 5,
      },
      billing: {
        items: [
          { id:"i1", sut_code: "SUT010", desc: "Laparoskopik Apendektomi", qty: 1, unit_price: 18000, amount: 18000, date: "2026-02-10" },
          { id:"i2", sut_code: "SUT011", desc: "Ameliyathane kullanım ücreti", qty: 1, unit_price: 5000, amount: 5000, date: "2026-02-10" },
          { id:"i3", sut_code: "SUT012", desc: "Anestezi ücreti", qty: 1, unit_price: 4500, amount: 4500, date: "2026-02-10" },
          { id:"i4", sut_code: "SUT013", desc: "Lap set (trokar/port disposable)", qty: 1, unit_price: 3200, amount: 3200, date: "2026-02-10" },
          { id:"i5", sut_code: "SUT001", desc: "Standart yatış günü", qty: 5, unit_price: 1500, amount: 7500, date: "2026-02-10" },
        ]
      },
      policy: { limits: { daily_room_max: 2000 } }
    },
    history: []
  },
  cesarean_readmit: {
    label: "🔄 TR · Sezaryen — 30 Gün İçi Tekrar Yatış",
    country: "TR",
    data: {
      case_id: "TR-006", patient: { age: 34, sex: "K" },
      provider: { name: "Özel Doğum Hastanesi" },
      dates: { admit: "2026-02-15", discharge: "2026-02-23" },
      icd10_primary: "O82", icd10_secondary: ["O86"],
      clinical: {
        delivery_note_present: true,
        cs_indication_documented: true,
        ctg_or_partogram: false,
        complications: { pph: false, infection: true, severe_preeclampsia: false },
        nicu_days: 0,
        procedures_np: ["NP101"],
        actual_los: 8,
      },
      billing: {
        daily_room_price: 2200,
        items: [
          { sut_code: "SUT020", desc: "Sezaryen", qty: 1, unit_price: 25000, amount: 25000, date: "2026-02-15" },
          { sut_code: "SUT021", desc: "Ameliyathane", qty: 1, unit_price: 4000, amount: 4000, date: "2026-02-15" },
          { sut_code: "SUT001", desc: "Yatış günü", qty: 8, unit_price: 2200, amount: 17600, date: "2026-02-15" },
        ]
      },
      policy: { limits: { daily_room_max: 2000 } }
    },
    history: [
      { case_id: "TR-004", icd10_primary: "O82", dates: { admit: "2026-01-20", discharge: "2026-01-28" } }
    ]
  },
  nl_cap: {
    label: "🇳🇱 NL · Pnömoni — DBC Denetimi",
    country: "NL",
    data: {
      case_id: "NL-001", patient: { age: 65, sex: "E" },
      provider: { name: "Amsterdam UMC" },
      dates: { admit: "2026-01-08", discharge: "2026-01-20" },
      dbc_code: "14B780",
      icd10_primary: "J18.9", icd10_secondary: ["E11.9"],
      actual_los: 12,
      claimed_amount: 8400,
      hoofddiagnose_present: true,
      zorgactiviteiten_present: false,
      ontslagbrief_present: true,
    }
  },
  nl_cesarean: {
    label: "🇳🇱 NL · Sezaryen — DBC + Komplikasyon",
    country: "NL",
    data: {
      case_id: "NL-002", patient: { age: 31, sex: "K" },
      provider: { name: "Erasmus MC" },
      dates: { admit: "2026-02-01", discharge: "2026-02-09" },
      dbc_code: "17B032",
      icd10_primary: "O82", icd10_secondary: ["O72.0","O14.1"],
      actual_los: 8,
      claimed_amount: 12600,
      hoofddiagnose_present: true,
      zorgactiviteiten_present: true,
      ontslagbrief_present: false,
    }
  },
};

// ═══════════════════════════════════════════════════════════════
// UI BİLEŞENLERİ
// ═══════════════════════════════════════════════════════════════
const C = {
  bg:      "#060c14",
  surface: "#090f1c",
  card:    "#0b1526",
  border:  "#1a2a40",
  text:    "#d4dff0",
  muted:   "#4a6080",
  accent:  "#1a6cf0",
  green:   "#22c55e", greenDim: "#0d2a16",
  yellow:  "#f59e0b", yellowDim: "#231a08",
  red:     "#ef4444", redDim:   "#220a0a",
  blue:    "#3b82f6", blueDim:  "#0a1630",
};

const pill = (color, bg, text) => ({
  display:"inline-flex", alignItems:"center",
  background: bg, border: `1px solid ${color}`,
  color, borderRadius: 6, padding: "2px 10px",
  fontSize: 11, fontWeight: 700, letterSpacing: 0.5,
});

const Flag = ({ level, msg }) => {
  const map = {
    ok:   { color: C.green,  bg: C.greenDim,  icon: "✓" },
    warn: { color: C.yellow, bg: C.yellowDim, icon: "⚠" },
    error:{ color: C.red,    bg: C.redDim,    icon: "✗" },
  };
  const m = map[level] || map.ok;
  return (
    <div style={{
      display:"flex", gap:10, alignItems:"flex-start",
      padding:"8px 12px", borderRadius:8, marginBottom:5,
      background: m.bg, borderLeft:`3px solid ${m.color}`,
    }}>
      <span style={{ color:m.color, fontWeight:700, minWidth:14 }}>{m.icon}</span>
      <span style={{ color: C.text, fontSize:12.5 }}>{msg}</span>
    </div>
  );
};

const Sec = ({ title, icon, children, accent }) => (
  <div style={{ marginBottom: 20 }}>
    <div style={{
      display:"flex", alignItems:"center", gap:8,
      color: accent || C.muted, fontSize:10, fontWeight:700, letterSpacing:2,
      textTransform:"uppercase", marginBottom:9,
      paddingBottom:6, borderBottom:`1px solid ${C.border}`,
    }}>
      <span>{icon}</span>{title}
    </div>
    {children}
  </div>
);

const StatBox = ({ label, value, color, sub }) => (
  <div style={{ background: C.card, border:`1px solid ${C.border}`, borderRadius:10, padding:"14px 18px" }}>
    <div style={{ color: C.muted, fontSize:10, letterSpacing:1, marginBottom:5 }}>{label}</div>
    <div style={{ color: color || C.text, fontSize:20, fontWeight:700 }}>{value}</div>
    {sub && <div style={{ color: C.muted, fontSize:11, marginTop:3 }}>{sub}</div>}
  </div>
);

// ═══════════════════════════════════════════════════════════════
// ANA UYGULAMA
// ═══════════════════════════════════════════════════════════════
export default function ClaimGuard() {
  const [selected, setSelected] = useState(null);
  const [result,   setResult]   = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [tab,      setTab]      = useState("home");
  const [nlMode,   setNlMode]   = useState(false);

  const runCase = useCallback((key) => {
    const sc = SAMPLE_CASES[key];
    setSelected(key);
    setLoading(true);
    setResult(null);
    setTab("report");
    setTimeout(() => {
      const r = sc.country === "NL"
        ? runNLAudit(sc.data)
        : runTRAudit(sc.data, sc.history || []);
      r._country = sc.country;
      setResult(r);
      setLoading(false);
    }, 750);
  }, []);

  const fmt = (n) => (n || 0).toLocaleString("tr-TR") + " ₺";
  const fmtEur = (n) => "€ " + (n || 0).toLocaleString("nl-NL");

  const decisionPalette = (dc) => ({
    green:  { bg: C.greenDim,  border: C.green,  text: C.green },
    orange: { bg: C.yellowDim, border: C.yellow, text: C.yellow },
    red:    { bg: C.redDim,    border: C.red,    text: C.red },
  }[dc] || { bg: C.blueDim, border: C.blue, text: C.blue });

  return (
    <div style={{ minHeight:"100vh", background: C.bg, fontFamily:"'IBM Plex Mono','Fira Code',monospace", color: C.text }}>

      {/* ── HEADER ── */}
      <div style={{
        background:`linear-gradient(135deg,${C.surface},${C.card})`,
        borderBottom:`1px solid ${C.border}`,
        padding:"14px 24px",
        display:"flex", alignItems:"center", justifyContent:"space-between",
      }}>
        <div style={{ display:"flex", alignItems:"center", gap:14 }}>
          <div style={{
            width:40, height:40, borderRadius:10,
            background:"linear-gradient(135deg,#1a4cf0,#0ea5e9)",
            display:"flex", alignItems:"center", justifyContent:"center", fontSize:20,
          }}>⚕</div>
          <div>
            <div style={{ fontSize:15, fontWeight:700, letterSpacing:1, color:"#e0f4ff" }}>ClaimGuard AI</div>
            <div style={{ fontSize:10, color: C.muted, letterSpacing:2 }}>SAĞLIK SİGORTA DENETİM MOTORİ — TR + NL — v2.0</div>
          </div>
        </div>
        <div style={{ display:"flex", gap:6, flexWrap:"wrap", justifyContent:"flex-end" }}>
          {["SGK SUT 2025","NICE NG192","SWAB CAP 2024","ICD-10","DBC/DOT"].map(b => (
            <span key={b} style={pill(C.blue, C.blueDim, C.blue)}>{b}</span>
          ))}
        </div>
      </div>

      <div style={{ display:"flex", minHeight:"calc(100vh - 69px)" }}>

        {/* ── SOL PANEL ── */}
        <div style={{
          width:270, background: C.surface,
          borderRight:`1px solid ${C.border}`, padding:"18px 14px",
          flexShrink:0, overflowY:"auto",
        }}>
          <div style={{ color: C.muted, fontSize:10, letterSpacing:2, marginBottom:10, fontWeight:700 }}>ÖRNEK VAKALAR</div>
          {Object.entries(SAMPLE_CASES).map(([key, sc]) => (
            <button key={key} onClick={() => runCase(key)} style={{
              width:"100%", textAlign:"left", padding:"10px 12px",
              borderRadius:9, marginBottom:6, cursor:"pointer",
              background: selected === key ? C.blueDim : C.card,
              border: selected === key ? `1px solid ${C.blue}` : `1px solid ${C.border}`,
              color: selected === key ? "#90c8ff" : C.muted,
              fontSize:11.5, lineHeight:1.5, transition:"all 0.15s",
            }}>{sc.label}</button>
          ))}

          {/* Kapsam */}
          <div style={{ marginTop:20, background: C.card, borderRadius:10, padding:14, border:`1px solid ${C.border}` }}>
            <div style={{ color: C.muted, fontSize:10, letterSpacing:2, marginBottom:10, fontWeight:700 }}>🇹🇷 TR KAPSAMI</div>
            {[["J12-J18","CAP Pnömoni"],["K35-K37","Apandisit"],["O82","Sezaryen"]].map(([c,l]) => (
              <div key={c} style={{ display:"flex", justifyContent:"space-between", marginBottom:5 }}>
                <span style={{ color: C.blue, fontSize:11 }}>{c}</span>
                <span style={{ color: C.muted, fontSize:11 }}>{l}</span>
              </div>
            ))}
            <div style={{ color: C.muted, fontSize:10, letterSpacing:2, margin:"12px 0 8px", fontWeight:700 }}>🇳🇱 NL KAPSAMI</div>
            {[["14B780","CAP – klinisch"],["11C360","Appendectomie lap"],["17B030","Keizersnede"]].map(([c,l]) => (
              <div key={c} style={{ display:"flex", justifyContent:"space-between", marginBottom:5 }}>
                <span style={{ color:"#a78bfa", fontSize:11 }}>{c}</span>
                <span style={{ color: C.muted, fontSize:11 }}>{l}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ── SAĞ ── */}
        <div style={{ flex:1, padding:22, overflowY:"auto" }}>

          {/* TABS */}
          <div style={{ display:"flex", gap:4, marginBottom:22 }}>
            {[["home","◈ Ana Sayfa"],["report","⊞ Denetim Raporu"],["matrix","⊟ Kural Matrisi"],["nl","🇳🇱 NL Mapping"]].map(([t,l]) => (
              <button key={t} onClick={() => setTab(t)} style={{
                padding:"7px 16px", borderRadius:8, cursor:"pointer", fontSize:12, fontWeight:600,
                background: tab === t ? C.accent : "transparent",
                border: tab === t ? `1px solid ${C.blue}` : `1px solid ${C.border}`,
                color: tab === t ? "#fff" : C.muted, letterSpacing:0.3,
              }}>{l}</button>
            ))}
          </div>

          {/* ── LOADING ── */}
          {loading && (
            <div style={{ textAlign:"center", padding:60 }}>
              <div style={{ fontSize:36, marginBottom:14, display:"inline-block", animation:"spin 0.9s linear infinite" }}>⟳</div>
              <div style={{ color: C.blue, fontSize:13, letterSpacing:2 }}>KURAL MOTORU ÇALIŞIYOR…</div>
              <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
            </div>
          )}

          {/* ─── HOME TAB ─── */}
          {!loading && tab === "home" && (
            <div>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:14, marginBottom:22 }}>
                <StatBox label="Modüller" value="7 Aktif" color={C.green} sub="CC/MCC · Paket · Tekrar Yatış · NL"/>
                <StatBox label="Protokol" value="TR+NL" color="#a78bfa" sub="SUT 2025 + DBC/DOT"/>
                <StatBox label="ICD-10 Kapsam" value="25+ Kod" color={C.blue} sub="Genişletilebilir"/>
              </div>

              <div style={{ background: C.card, border:`1px solid ${C.border}`, borderRadius:12, padding:22 }}>
                <div style={{ color: C.blue, fontSize:12, fontWeight:700, marginBottom:16, letterSpacing:1 }}>MOTOR MODÜLLERİ</div>
                {[
                  ["1","ICD-10 Eşleme",     "Tanı kodu → Kural seti + ülke seçimi"],
                  ["2","CC/MCC Analizi",    "Komorbidite ağırlığı → dinamik LOS beklentisi"],
                  ["3","Yatış Endikasyonu", "CURB-65 / klinik kriterler / NICE protokolü"],
                  ["4","LOS Denetimi",      "Beklenen vs gerçek yatış (CC/MCC düzeltmeli)"],
                  ["5","Fatura Denetimi",   "Mükerrer · Oda limiti · YBÜ çapraz kontrolü"],
                  ["6","Paket Çakışması",   "SUT paket içi kalemlerin ayrıca faturalandırılması"],
                  ["7","Tekrar Yatış",      "30/90 gün aynı tanıyla tekrar yatış tespiti"],
                  ["8","NL DBC/DOT",        "Hollanda DBC kodu → NP eşleme + NL LOS kuralları"],
                  ["9","Eksik Belge",       "Kanıt listesi → 'Hastaneden talep et' çıktısı"],
                ].map(([n,title,desc]) => (
                  <div key={n} style={{
                    display:"flex", gap:12, alignItems:"flex-start",
                    padding:"10px 0", borderBottom:`1px solid ${C.border}`,
                  }}>
                    <div style={{
                      width:26, height:26, borderRadius:"50%", flexShrink:0,
                      background: C.accent, display:"flex", alignItems:"center",
                      justifyContent:"center", fontSize:11, fontWeight:700, color:"#fff",
                    }}>{n}</div>
                    <div>
                      <div style={{ color:"#c4dfff", fontSize:13, fontWeight:600 }}>{title}</div>
                      <div style={{ color: C.muted, fontSize:12, marginTop:2 }}>{desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ─── RAPOR TAB ─── */}
          {!loading && tab === "report" && result && !result.error && (() => {
            const isNL = result.country === "NL";
            const pal = decisionPalette(result.decisionColor);
            const currency = isNL ? fmtEur : fmt;

            return (
              <div>
                {/* Karar başlığı */}
                <div style={{
                  background: pal.bg, border:`1px solid ${pal.border}`,
                  borderRadius:14, padding:"20px 24px", marginBottom:18,
                  display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:14,
                }}>
                  <div>
                    <div style={{ fontSize:10, color: C.muted, letterSpacing:2, marginBottom:5 }}>
                      {isNL ? "NL DBC DENETİM KARARI" : "TR SUT DENETİM KARARI"}
                    </div>
                    <div style={{ fontSize:22, fontWeight:700, color: pal.text }}>{result.decision}</div>
                    <div style={{ color: C.muted, fontSize:12, marginTop:6 }}>
                      {isNL
                        ? <>DBC: <span style={{ color:"#a78bfa" }}>{result.dbc}</span> — {result.mapping?.label}</>
                        : <>{result.label} | ICD-10: <span style={{ color: C.blue }}>{SAMPLE_CASES[selected]?.data?.icd10_primary}</span></>
                      }
                      {" "}| LOS: {result.actualLOS}g (beklenti: {result.expectedLOS}g)
                    </div>
                    {!isNL && result.ccmcc?.all?.length > 0 && (
                      <div style={{ marginTop:8, display:"flex", gap:6, flexWrap:"wrap" }}>
                        {result.ccmcc.mcc.map(m => <span key={m.code} style={pill(C.red,C.redDim,C.red)}>MCC: {m.label}</span>)}
                        {result.ccmcc.cc.map(m  => <span key={m.code} style={pill(C.yellow,C.yellowDim,C.yellow)}>CC: {m.label}</span>)}
                      </div>
                    )}
                  </div>
                  <div style={{ textAlign:"right" }}>
                    <div style={{ color: C.green, fontSize:10, letterSpacing:1 }}>ÖDENEBİLİR</div>
                    <div style={{ fontSize:24, fontWeight:700, color: C.green }}>{currency(result.payAmount)}</div>
                    {result.holdAmount > 0 && <>
                      <div style={{ color: C.red, fontSize:10, letterSpacing:1, marginTop:8 }}>ASKIDA</div>
                      <div style={{ fontSize:18, fontWeight:700, color: C.red }}>{currency(result.holdAmount)}</div>
                    </>}
                  </div>
                </div>

                {/* Tutar özet */}
                <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:12, marginBottom:20 }}>
                  <StatBox label="Talep" value={currency(result.totalClaimed || result.totalBilled)} />
                  <StatBox label="Ödenebilir" value={currency(result.payAmount)} color={C.green}/>
                  <StatBox label="Askıda" value={currency(result.holdAmount)} color={result.holdAmount > 0 ? C.red : C.muted}/>
                </div>

                {/* TR — Tutarların dökümü */}
                {!isNL && (result.pkgHold > 0 || result.roomHold > 0 || result.losHold > 0) && (
                  <Sec title="Kesinti Dökümü" icon="◫" accent={C.red}>
                    {result.pkgHold  > 0 && <Flag level="error" msg={`Paket çakışması kesinti: ${fmt(result.pkgHold)}`}/>}
                    {result.roomHold > 0 && <Flag level="warn"  msg={`Oda limiti aşım kesinti: ${fmt(result.roomHold)}`}/>}
                    {result.losHold  > 0 && <Flag level="error" msg={`Gerekçesiz uzamış LOS kesinti: ${fmt(result.losHold)}`}/>}
                  </Sec>
                )}

                {/* Yatış endikasyonu */}
                {!isNL && (
                  <Sec title="Yatış Endikasyonu" icon="⊕">
                    {result.admissionFlags.map((f,i) => <Flag key={i} level={f.ok ? "ok" : "error"} msg={f.msg}/>)}
                  </Sec>
                )}

                {/* ICU */}
                {!isNL && result.icuFlags?.length > 0 && (
                  <Sec title="YBÜ Endikasyonu" icon="◉">
                    {result.icuFlags.map((f,i) => <Flag key={i} level={f.ok ? "ok":"error"} msg={f.msg}/>)}
                  </Sec>
                )}

                {/* CC/MCC */}
                {!isNL && (
                  <Sec title="CC/MCC Analizi" icon="⊠" accent={result.ccmcc?.all?.length > 0 ? C.yellow : C.muted}>
                    {result.ccmcc?.all?.length === 0
                      ? <Flag level="ok" msg={`CC/MCC yok — Baz LOS: ${result.baseLOS}g kullanıldı`}/>
                      : result.ccmcc.all.map((m,i) => (
                          <Flag key={i} level={m.level==="MCC"?"error":"warn"}
                            msg={`${m.level}: ${m.label} (${m.code}) → LOS +${m.los_add}g eklendi`}/>
                        ))
                    }
                    {result.ccmcc?.all?.length > 0 &&
                      <Flag level="ok" msg={`Toplam CC/MCC LOS düzeltmesi: +${result.ccmcc.los_add}g → Beklenti ${result.baseLOS}+${result.ccmcc.los_add}=${result.expectedLOS}g`}/>
                    }
                  </Sec>
                )}

                {/* LOS */}
                <Sec title="LOS Analizi" icon="◷">
                  {(result.losFlags || result.flags || []).filter(f=>f.msg||f.level).map((f,i) => (
                    <Flag key={i} level={f.level} msg={f.msg}/>
                  ))}
                </Sec>

                {/* Fatura */}
                {!isNL && (
                  <Sec title="Fatura Denetimi" icon="◫">
                    {result.billingFlags?.length === 0
                      ? <Flag level="ok" msg="Faturada anormallik tespit edilmedi"/>
                      : result.billingFlags.map((f,i) => <Flag key={i} level={f.level} msg={f.msg}/>)
                    }
                    {result.active_pkg && (
                      <div style={{ marginTop:8, padding:"8px 12px", background: C.blueDim, borderRadius:8, color: C.blue, fontSize:12 }}>
                        Aktif paket: {result.active_pkg.label}
                      </div>
                    )}
                  </Sec>
                )}

                {/* NL flags */}
                {isNL && result.flags && (
                  <Sec title="DBC Denetim Bulguları" icon="🇳🇱">
                    {result.flags.map((f,i) => <Flag key={i} level={f.level} msg={f.msg}/>)}
                  </Sec>
                )}

                {/* Tekrar yatış */}
                {!isNL && result.readmissionFlags?.length > 0 && (
                  <Sec title="Tekrar Yatış Kontrolü" icon="🔄" accent={C.red}>
                    {result.readmissionFlags.map((f,i) => <Flag key={i} level={f.level} msg={f.msg}/>)}
                  </Sec>
                )}

                {/* Eksik kanıt */}
                {result.missingEvidence?.length > 0 && (
                  <Sec title="Eksik Belgeler" icon="⊘" accent={C.red}>
                    {result.missingEvidence.map((e,i) => (
                      <Flag key={i} level="error" msg={`Eksik: ${e} → Hastaneden talep edilmeli`}/>
                    ))}
                  </Sec>
                )}

                {/* Kanıt kontrol listesi */}
                {result.requiredEvidence && (
                  <Sec title="Kanıt Kontrol Listesi" icon="⊟">
                    <div style={{ background: C.card, borderRadius:10, padding:14, border:`1px solid ${C.border}` }}>
                      {result.requiredEvidence.map((e,i) => {
                        const missing = result.missingEvidence?.includes(e);
                        return (
                          <div key={i} style={{
                            display:"flex", alignItems:"center", gap:8, padding:"6px 0",
                            borderBottom: i < result.requiredEvidence.length-1 ? `1px solid ${C.border}` : "none",
                            color: missing ? C.red : C.green, fontSize:13,
                          }}>
                            <span>{missing ? "✗" : "✓"}</span><span>{e}</span>
                          </div>
                        );
                      })}
                    </div>
                  </Sec>
                )}

                {/* Kaynaklar */}
                <Sec title="Kaynak Dayanaklar" icon="⊕">
                  <div style={{ background: C.card, borderRadius:10, padding:14, border:`1px solid ${C.border}` }}>
                    {[
                      ["SGK SUT 2025","TR faturalama, paket kuralları, fiyat limitleri"],
                      ["NICE NG192","Sezaryen endikasyonu ve perioperatif yönetim"],
                      ["NICE CAP","CURB-65, yatış kararı, bakım yeri seçimi"],
                      ["SWAB CAP 2024","Toplum kökenli pnömoni NL protokolü"],
                      ["NL DBC/DOT","Hollanda ödeme grupları ve zorgactiviteiten"],
                      ["WHO ICD-10","Tanı kodu standardizasyonu (TR+NL ortak)"],
                    ].map(([src,desc]) => (
                      <div key={src} style={{
                        display:"flex", justifyContent:"space-between", padding:"6px 0",
                        borderBottom:`1px solid ${C.border}`, fontSize:12,
                      }}>
                        <span style={{ color: C.blue, fontWeight:600 }}>{src}</span>
                        <span style={{ color: C.muted }}>{desc}</span>
                      </div>
                    ))}
                  </div>
                </Sec>
              </div>
            );
          })()}

          {!loading && tab === "report" && result?.error && (
            <div style={{ background: C.redDim, border:`1px solid ${C.red}`, borderRadius:12, padding:24, color: C.red }}>
              ✗ {result.message}
            </div>
          )}

          {!loading && tab === "report" && !result && (
            <div style={{ textAlign:"center", padding:60, color: C.muted }}>
              <div style={{ fontSize:40, marginBottom:12 }}>◈</div>
              <div>Sol panelden bir vaka seçin</div>
            </div>
          )}

          {/* ─── KURAL MATRİSİ TAB ─── */}
          {!loading && tab === "matrix" && (
            <div>
              {Object.entries(INDICATION_RULES).map(([key, rule]) => (
                <div key={key} style={{ background: C.card, border:`1px solid ${C.border}`, borderRadius:12, padding:20, marginBottom:16 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
                    <div>
                      <div style={{ fontSize:14, fontWeight:700, color:"#c4dfff" }}>{rule.label}</div>
                      <div style={{ color: C.muted, fontSize:11, marginTop:3 }}>{rule.icd10.join(" · ")}</div>
                    </div>
                    <span style={pill(C.blue, C.blueDim, C.blue)}>{rule.np_codes.join(" · ")}</span>
                  </div>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                    <div style={{ background: C.surface, borderRadius:8, padding:12 }}>
                      <div style={{ color: C.green, fontSize:10, letterSpacing:1.5, marginBottom:8, fontWeight:700 }}>ZORUNLU KANIT</div>
                      {rule.requiredEvidence.map((e,i) => (
                        <div key={i} style={{ color: C.muted, fontSize:12, padding:"3px 0", borderBottom:`1px solid ${C.border}` }}>• {e}</div>
                      ))}
                    </div>
                    {rule.ifICU && (
                      <div style={{ background: C.surface, borderRadius:8, padding:12 }}>
                        <div style={{ color: C.yellow, fontSize:10, letterSpacing:1.5, marginBottom:8, fontWeight:700 }}>YBÜ/KOMPLİKASYON KANITI</div>
                        {(rule.ifICU || rule.ifComplicated || []).map((e,i) => (
                          <div key={i} style={{ color: C.muted, fontSize:12, padding:"3px 0", borderBottom:`1px solid ${C.border}` }}>• {e}</div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {/* CC/MCC tablosu */}
              <div style={{ background: C.card, border:`1px solid ${C.border}`, borderRadius:12, padding:20 }}>
                <div style={{ fontSize:14, fontWeight:700, color:"#c4dfff", marginBottom:14 }}>CC/MCC Sözlüğü</div>
                <div style={{ overflowX:"auto" }}>
                  <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
                    <thead>
                      <tr style={{ color: C.muted, fontSize:10, letterSpacing:1 }}>
                        {["ICD-10","Seviye","Tanı","LOS Eklentisi"].map(h => (
                          <th key={h} style={{ textAlign:"left", padding:"6px 12px", borderBottom:`1px solid ${C.border}` }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(CC_MCC_MAP).map(([code, info]) => (
                        <tr key={code} style={{ borderBottom:`1px solid ${C.border}` }}>
                          <td style={{ padding:"7px 12px", color: C.blue }}>{code}</td>
                          <td style={{ padding:"7px 12px" }}>
                            <span style={pill(info.level==="MCC"?C.red:C.yellow, info.level==="MCC"?C.redDim:C.yellowDim, info.level==="MCC"?C.red:C.yellow)}>
                              {info.level}
                            </span>
                          </td>
                          <td style={{ padding:"7px 12px", color: C.text }}>{info.label}</td>
                          <td style={{ padding:"7px 12px", color: C.green }}>+{info.los_add}g</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* ─── NL MAPPING TAB ─── */}
          {!loading && tab === "nl" && (
            <div>
              <div style={{ background: C.card, border:`1px solid ${C.border}`, borderRadius:12, padding:20, marginBottom:16 }}>
                <div style={{ fontSize:14, fontWeight:700, color:"#c4dfff", marginBottom:6 }}>🇳🇱 DBC/DOT → Normalized Procedure Mapping</div>
                <div style={{ color: C.muted, fontSize:12, marginBottom:16 }}>
                  Hollanda DBC kodları TR NP kodlarıyla eşleştirilir. Her iki ülkede aynı kural motoru çalışır.
                </div>
                <div style={{ overflowX:"auto" }}>
                  <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
                    <thead>
                      <tr style={{ color: C.muted, fontSize:10, letterSpacing:1 }}>
                        {["DBC Kodu","Omschrijving","NP Kodu","ICD-10 Eşdeğeri","Baz LOS","CC +","MCC +"].map(h => (
                          <th key={h} style={{ textAlign:"left", padding:"6px 10px", borderBottom:`1px solid ${C.border}`, whiteSpace:"nowrap" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(NL_DBC_MAP).map(([code, info]) => {
                        const los = NL_EXPECTED_LOS[code];
                        return (
                          <tr key={code} style={{ borderBottom:`1px solid ${C.border}` }}>
                            <td style={{ padding:"7px 10px", color:"#a78bfa", fontWeight:600 }}>{code}</td>
                            <td style={{ padding:"7px 10px", color: C.text }}>{info.label}</td>
                            <td style={{ padding:"7px 10px" }}><span style={pill(C.blue,C.blueDim,C.blue)}>{info.np}</span></td>
                            <td style={{ padding:"7px 10px", color: C.muted }}>{info.icd10_equiv || "—"}</td>
                            <td style={{ padding:"7px 10px", color: C.green }}>{los ? los.base+"g" : "—"}</td>
                            <td style={{ padding:"7px 10px", color: C.yellow }}>{los ? "+"+los.cc_add+"g" : "—"}</td>
                            <td style={{ padding:"7px 10px", color: C.red }}>{los ? "+"+los.mcc_add+"g" : "—"}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <div style={{ background: C.card, border:`1px solid ${C.border}`, borderRadius:12, padding:20 }}>
                <div style={{ fontSize:13, fontWeight:700, color:"#c4dfff", marginBottom:12 }}>NL Özel Denetim Kuralları</div>
                {[
                  ["Hoofddiagnose","DBC beyanında ana tanı zorunlu","error"],
                  ["Zorgactiviteiten","Tüm hizmet aktiviteleri tam listelenmiş olmalı","warn"],
                  ["Ontslagbrief","NL sigorta için taburculuk mektubu zorunlu","warn"],
                  ["LOS Trigger","Beklenen LOS + 1 günden fazlası → inceleme tetikler","error"],
                  ["CC/MCC","İkincil tanılar DBC LOS tablosuyla çapraz kontrol edilir","info"],
                ].map(([title, desc, level]) => (
                  <div key={title} style={{
                    display:"flex", gap:12, alignItems:"flex-start",
                    padding:"10px 0", borderBottom:`1px solid ${C.border}`,
                  }}>
                    <span style={pill(level==="error"?C.red:level==="warn"?C.yellow:C.blue,
                      level==="error"?C.redDim:level==="warn"?C.yellowDim:C.blueDim,
                      level==="error"?C.red:level==="warn"?C.yellow:C.blue)}>{level.toUpperCase()}</span>
                    <div>
                      <div style={{ color:"#c4dfff", fontWeight:600, fontSize:13 }}>{title}</div>
                      <div style={{ color: C.muted, fontSize:12, marginTop:2 }}>{desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
